import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PullToRefresh } from '@/components/PullToRefresh';
import { 
  Users, Search, Shield, ShieldCheck, ShieldAlert, User, Crown, Plane, 
  UserCog, Trash2, MoreVertical, AlertTriangle, CheckCircle2 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_CONFIG = {
  admin: { 
    icon: Crown, 
    color: 'text-amber-400', 
    bg: 'bg-amber-500/10 border-amber-500/20',
    description: 'Full system access'
  },
  hofo: { 
    icon: ShieldCheck, 
    color: 'text-emerald-400', 
    bg: 'bg-emerald-500/10 border-emerald-500/20',
    description: 'Head of Flying Operations'
  },
  pilot: { 
    icon: Plane, 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/10 border-blue-500/20',
    description: 'Pilot access'
  },
  member: { 
    icon: User, 
    color: 'text-slate-400', 
    bg: 'bg-slate-500/10 border-slate-500/20',
    description: 'Standard member'
  }
};

export default function UserManagementPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [updating, setUpdating] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/users`, { credentials: 'include' });
      if (res.ok) {
        setUsers(await res.json());
      } else if (res.status === 403) {
        // Not admin - show only current user
        setUsers([currentUser]);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/roles`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRoles(data.roles);
      }
    } catch (err) {
      console.error('Fetch roles error:', err);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await fetchUsers();
    await fetchRoles();
    toast.success('Users refreshed');
  }, [fetchUsers, fetchRoles]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    setUpdating(true);
    try {
      const res = await fetch(`${API}/users/${selectedUser.user_id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success(`Role updated to ${newRole}`);
        setRoleDialogOpen(false);
        await fetchUsers();
        if (selectedUser.user_id === currentUser?.user_id) {
          refreshUser?.();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Request failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setUpdating(true);
    try {
      const res = await fetch(`${API}/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('User deleted');
        setDeleteDialogOpen(false);
        await fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('Request failed');
    } finally {
      setUpdating(false);
    }
  };

  const handlePromoteToAdmin = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/users/promote-to-admin`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('You are now an admin!');
        setPromoteDialogOpen(false);
        refreshUser?.();
        await fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to promote');
      }
    } catch (err) {
      toast.error('Request failed');
    } finally {
      setUpdating(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || 'member');
    setRoleDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="user-management-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-2 sm:gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Users className="h-6 w-6 sm:h-7 sm:w-7 text-[#0ea5e9]" />
              User Management
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              Manage user roles and permissions
            </p>
          </div>
          
          {/* Current user role badge */}
          <div className="flex items-center gap-2">
            {!isAdmin && !users.some(u => u.role === 'admin') && (
              <Button
                data-testid="become-admin-btn"
                onClick={() => setPromoteDialogOpen(true)}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Crown className="h-4 w-4 mr-2" />
                Become Admin
              </Button>
            )}
            <Badge className={`${ROLE_CONFIG[currentUser?.role]?.bg || ROLE_CONFIG.member.bg} text-xs`}>
              {currentUser?.role?.toUpperCase() || 'MEMBER'}
            </Badge>
          </div>
        </div>

        {/* Search */}
        {isAdmin && (
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="user-search"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
            />
          </div>
        )}

        {/* Role Legend */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(ROLE_CONFIG).map(([roleId, config]) => {
            const RoleIcon = config.icon;
            return (
              <div key={roleId} className="flex items-center gap-1.5 text-xs">
                <RoleIcon className={`h-3.5 w-3.5 ${config.color}`} />
                <span className="text-slate-500 capitalize">{roleId}</span>
              </div>
            );
          })}
        </div>

        {/* Users List */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-[120px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : !isAdmin ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-center">
              <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <p className="text-slate-300 font-medium">Admin Access Required</p>
              <p className="text-sm text-slate-500 mt-1">
                Only administrators can manage users. Contact an admin for role changes.
              </p>
              {!users.some(u => u.role === 'admin') && (
                <Button
                  onClick={() => setPromoteDialogOpen(true)}
                  className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Become First Admin
                </Button>
              )}
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-500">No users found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filtered.map((user) => {
              const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.member;
              const RoleIcon = roleConfig.icon;
              const isCurrentUser = user.user_id === currentUser?.user_id;
              
              return (
                <Card
                  key={user.user_id}
                  data-testid={`user-card-${user.user_id}`}
                  className={`bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-colors ${
                    isCurrentUser ? 'ring-1 ring-[#0ea5e9]/50' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="relative">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center">
                            <User className="h-5 w-5 text-slate-500" />
                          </div>
                        )}
                        <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full ${roleConfig.bg} flex items-center justify-center`}>
                          <RoleIcon className={`h-3 w-3 ${roleConfig.color}`} />
                        </div>
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-200 truncate">{user.name}</p>
                          {isCurrentUser && (
                            <Badge className="bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/20 text-[9px]">YOU</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`${roleConfig.bg} text-[10px] capitalize`}>
                            {user.role || 'member'}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      {isAdmin && !isCurrentUser && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRoleDialog(user)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-[#0ea5e9]"
                            title="Change role"
                          >
                            <UserCog className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(user)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-red-400"
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Role description */}
                    <p className="text-[10px] text-slate-600 mt-3 pt-3 border-t border-slate-800/50">
                      {roleConfig.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Change Role Dialog */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full bg-[#0f172a] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-slate-200 flex items-center gap-2">
                <UserCog className="h-5 w-5 text-[#0ea5e9]" />
                Change User Role
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                Update role for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                {selectedUser?.picture ? (
                  <img src={selectedUser.picture} alt="" className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-200">{selectedUser?.name}</p>
                  <p className="text-xs text-slate-500">{selectedUser?.email}</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 font-mono uppercase tracking-wider mb-2 block">
                  Select Role
                </label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f172a] border-slate-700">
                    {roles.map(role => {
                      const config = ROLE_CONFIG[role.id] || ROLE_CONFIG.member;
                      const RoleIcon = config.icon;
                      return (
                        <SelectItem 
                          key={role.id} 
                          value={role.id}
                          className="text-slate-300 focus:bg-slate-800"
                        >
                          <div className="flex items-center gap-2">
                            <RoleIcon className={`h-4 w-4 ${config.color}`} />
                            <span className="capitalize">{role.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {newRole && ROLE_CONFIG[newRole] && (
                <div className={`p-3 rounded-lg ${ROLE_CONFIG[newRole].bg}`}>
                  <p className="text-xs text-slate-300">
                    <strong className="capitalize">{newRole}</strong>: {ROLE_CONFIG[newRole].description}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setRoleDialogOpen(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button
                onClick={handleUpdateRole}
                disabled={updating || newRole === selectedUser?.role}
                className="bg-[#003087] hover:bg-[#003087]/90 text-white"
              >
                {updating ? 'Updating...' : 'Update Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full bg-[#0f172a] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-slate-200 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                Delete User
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                {selectedUser?.picture ? (
                  <img src={selectedUser.picture} alt="" className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-500" />
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-200">{selectedUser?.name}</p>
                  <p className="text-xs text-slate-500">{selectedUser?.email}</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-4">
                Are you sure you want to delete this user? All their data and sessions will be removed.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={updating}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {updating ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Promote to Admin Dialog */}
        <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full bg-[#0f172a] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-slate-200 flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-400" />
                Become Administrator
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                No admin exists yet. You can become the first admin.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-slate-300">
                  As an administrator, you will have full access to:
                </p>
                <ul className="mt-2 space-y-1 text-xs text-slate-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                    Manage all users and their roles
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                    Full access to all documents and features
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                    System configuration and settings
                  </li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setPromoteDialogOpen(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button
                onClick={handlePromoteToAdmin}
                disabled={updating}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {updating ? 'Promoting...' : 'Become Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PullToRefresh>
  );
}
